import { Component, Inject, OnInit, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ToolbarService } from '../../../services/toolbar.service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { urlValidator } from '../../../validators/urlValidator.validator';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { QuotingSystemOAuth2 } from '../../../model/quoting-system/quoting-system-oAuth.model';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { SysAdminService } from '../../../services/sys-admin.service';
import { mediumModalConfig } from '../../../consts/modal-config.const';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';

@Component({
  selector: 'app-configure-o-auth-interface',
  templateUrl: './configure-o-auth-interface.component.html',
  styleUrls: ['./configure-o-auth-interface.component.scss'],
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    MatDialogActions,
    MatButton
  ]
})
export class ConfigureOAuthInterfaceComponent implements OnInit {
  oAuthForm: UntypedFormGroup;
  pending = signal<boolean>(false);
  isProd = signal<boolean>(false);

  constructor(
    public dialogRef: MatDialogRef<ConfigureOAuthInterfaceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    public toolbarService: ToolbarService,
    private message: MessagingService,
    private confirmBox: MatDialog,
    private escapeDialogRef: EscapeDialogHelper,
    private sysAdminService: SysAdminService,
    private modalConfigsHelper: ModalConfigsHelper
  ) {}

  get clientId() {
    return this.oAuthForm.get('clientId');
  }

  get clientSecret() {
    return this.oAuthForm.get('clientSecret');
  }

  get redirectUri() {
    return this.oAuthForm.get('redirectUri');
  }

  ngOnInit(): void {
    this.checkProductionMode();
    this.populateOAuthForm();

    this.clientSecret.valueChanges.subscribe(() => {
      if (this.clientSecret.invalid) {
        this.clientSecret.markAsTouched();
      }
    });

    this.redirectUri.valueChanges.subscribe(() => {
      if (this.redirectUri.invalid) {
        this.redirectUri.markAsTouched();
      }
    });

    this.escapeDialogRef.escapeDialog(this.dialogRef);
  }

  populateOAuthForm() {
    this.oAuthForm = this.fb.group({
      clientId: [{ value: this.data ? this.data.clientId : '', disabled: true }],
      clientSecret: [this.data ? this.data.clientSecret : '', Validators.required],
      redirectUri: [this.data ? this.data.redirectUri : '', [Validators.required, urlValidator(this.isProd())]]
    });
  }

  checkProductionMode() {
    this.toolbarService.setCloudState('FETCHING');
    this.sysAdminService.productionMode().subscribe({
      next: (isProd) => this.isProd.set(isProd),
      error: () => this.message.error('Could not retrieve the production mode.')
    });
  }

  confirmChangeStatusOfInterface(enable: boolean) {
    enable = !enable;
    const data = {
      title: enable ? 'Enable Interface' : 'Disable Interface',
      alertMessage: !enable
        ? 'The interface between ' +
          this.data.companyName +
          ' and EstImage Online (' +
          window.location.hostname +
          ') will be disabled. ' +
          this.data.companyName +
          ' users will no longer be able to send work to their EstImage Online site.'
        : 'The interface between ' +
          this.data.companyName +
          ' and EstImage Online (' +
          window.location.hostname +
          ') will be enabled. Users will be able to authorise ' +
          this.data.companyName +
          ' to send quotes, images, etc to their EstImage Online site.',
      confirmButton: enable ? 'ENABLE' : 'DISABLE',
      hideCannotBeUndone: true
    };
    const dialogRef = this.confirmBox.open(
      ConfirmBoxComponent,
      this.modalConfigsHelper.buildMediumModalConfig(data, mediumModalConfig)
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'DISABLE' || result === 'ENABLE') {
        this.data.oAuth2Enabled = enable;
        this.updateQuotingSystemOauth2();
      }
    });
  }

  updateQuotingSystemOauth2() {
    this.pending.set(true);

    const Oauth2InterfaceData: QuotingSystemOAuth2 = {
      clientId: this.data.clientId,
      clientSecret: this.clientSecret.value.trim(),
      redirectUri: this.redirectUri.value.trim(),
      enable: this.data.oAuth2Enabled
    };

    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.updateQuotingSystemOauth2(Oauth2InterfaceData).subscribe(
      () => this.dialogRef.close('Success oAuth'),
      () => {
        this.message.error('Could not configure OAuth2 interface.');
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      },
      () => {
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      }
    );
  }
}
